import * as babylon from "@babylonjs/core";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";
import { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import {
  TopicIdToMessagePaths,
  TopicIdToMessagePathValues,
} from "../../../DataServer/messaging";
import {
  createVectorFieldMesh,
  GradientOptions,
  VECTOR_FIELD_BASE_WIDTH,
} from "../../assetBuilders/vectorField";
import * as components from "../../components";
import { BabylonContext, BabylonState } from "../../state";

/**
 * Loads vector data for any vector fields whose caches have gone stale.
 */
export function reloadStaleVectorFieldCaches(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.vectorField ||
      entity.vectorField.cache.load.state !== "stale"
    ) {
      continue;
    }
    const vectorField = entity.vectorField;
    // Abort any previous load
    vectorField.cache.load.abortController.abort();

    vectorField.cache.load.state = "loading";
    const abortController = new AbortController();
    vectorField.cache.load.abortController = abortController;
    vectorField.cache.allVectors = [];

    reloadVectorCache(vectorField, state.context, abortController.signal).catch(
      (e) => {
        if (isAbortError(e) && abortController.signal.aborted) {
          return;
        }
        ErrorMonitoringService.captureError(e);
        vectorField.cache.load.state = "stale";
      },
    );
  }
}

/**
 * Builds the vector field mesh for entities whose vector and position caches
 * are both loaded and whose mesh is stale.
 */
export function reloadStaleVectorFieldMeshes(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.vectorField ||
      !entity.position ||
      !entity.vectorField.meshStale ||
      entity.vectorField.cache.load.state !== "loaded" ||
      entity.position.cache.load.state !== "loaded"
    ) {
      continue;
    }

    const vf = entity.vectorField;
    vf.mesh?.dispose();
    vf.currentMesh?.dispose();

    const mean = entity.position.mean;
    const allPositions = entity.position.cache.allPoints.map((p) =>
      p.subtract(mean),
    );
    const { alignedPositions, alignedVectors } = alignVectorsToPositions(
      entity.position.cache.logtimes,
      allPositions,
      vf.cache.logtimes,
      vf.cache.allVectors,
    );

    const stride = vf.spec.stride ?? 1;
    const lineWidth = vf.spec.lineWidth;

    const displayMode = vf.spec.displayMode ?? "both";
    const showField = displayMode === "both" || displayMode === "field_only";
    const showCurrent =
      displayMode === "both" || displayMode === "current_only";

    // Build gradient options if the user has enabled gradient mode.
    let gradient: GradientOptions | undefined;
    if (vf.spec.gradientEnabled && vf.spec.colorLow && vf.spec.colorHigh) {
      let minMag = Infinity;
      let maxMag = -Infinity;
      for (let i = 0; i < alignedVectors.length; i += stride) {
        const mag = alignedVectors[i].length();
        if (mag < minMag) {
          minMag = mag;
        }
        if (mag > maxMag) {
          maxMag = mag;
        }
      }
      gradient = {
        colorLow: babylon.Color3.FromHexString(vf.spec.colorLow),
        colorHigh: babylon.Color3.FromHexString(vf.spec.colorHigh),
        minMagnitude: minMag,
        maxMagnitude: maxMag,
      };
    }

    if (
      lineWidth > 0 &&
      isFinite(lineWidth) &&
      alignedPositions.length > 0 &&
      alignedVectors.length > 0
    ) {
      if (showField) {
        vf.mesh = createVectorFieldMesh(
          `${entity.id}.vectorField`,
          vf.spec.color,
          lineWidth,
          alignedPositions,
          alignedVectors,
          stride,
          state.context.scene,
          gradient,
        );
        if (vf.mesh.material) {
          vf.mesh.material.alpha = 0.5;
        }
      }

      if (showCurrent) {
        // Create a single-segment greased line for the current-time vector indicator.
        // Its geometry is updated every frame in the render loop.
        // Rendered as a triangle: fat base (10× width) tapering to a point.
        const initialSegment = [babylon.Vector3.Zero(), babylon.Vector3.Zero()];
        const currentWidths = [
          VECTOR_FIELD_BASE_WIDTH,
          VECTOR_FIELD_BASE_WIDTH,
          0,
          0,
        ];
        vf.currentMesh = babylon.CreateGreasedLine(
          `${entity.id}.vectorField.current`,
          {
            points: initialSegment,
            widths: currentWidths,
            widthDistribution:
              babylon.GreasedLineMeshWidthDistribution.WIDTH_DISTRIBUTION_NONE,
          },
          {
            color: babylon.Color3.FromHexString(vf.spec.color),
            width: vf.spec.lineWidth,
            sizeAttenuation: true,
          },
          state.context.scene,
        ) as babylon.GreasedLineMesh;
      }
    }
    vf.gradient = gradient;
    vf.meshStale = false;
  }
}

/**
 * Pairs each vector with its nearest position by timestamp.
 *
 * Returns parallel arrays of positions and vectors, one entry per vector timestamp.
 * Each vector is matched to the position with the closest timestamp at or before
 * the vector's timestamp, ensuring each vector appears exactly once.
 */
function alignVectorsToPositions(
  posLogtimes: bigint[],
  positions: babylon.Vector3[],
  vecLogtimes: bigint[],
  vectors: babylon.Vector3[],
): { alignedPositions: babylon.Vector3[]; alignedVectors: babylon.Vector3[] } {
  if (vecLogtimes.length === 0 || posLogtimes.length === 0) {
    return { alignedPositions: [], alignedVectors: [] };
  }
  const alignedPositions: babylon.Vector3[] = [];
  const alignedVectors: babylon.Vector3[] = [];
  let pi = 0;
  for (let vi = 0; vi < vecLogtimes.length && vi < vectors.length; vi++) {
    // Advance position index to the latest position at or before this vector time
    while (
      pi < posLogtimes.length - 1 &&
      posLogtimes[pi + 1] <= vecLogtimes[vi]
    ) {
      pi++;
    }
    alignedPositions.push(positions[pi]);
    alignedVectors.push(vectors[vi]);
  }
  return { alignedPositions, alignedVectors };
}

async function reloadVectorCache(
  vectorField: components.VectorField,
  context: BabylonContext,
  signal: AbortSignal,
) {
  const { right, up, forward, rightScale, upScale, forwardScale } =
    vectorField.spec;
  const toRead = [right, up, forward];

  // If all components are constants, produce a single constant vector.
  if (
    typeof right === "number" &&
    typeof forward === "number" &&
    typeof up === "number"
  ) {
    const vector = new babylon.Vector3(
      right * rightScale,
      up * upScale,
      forward * forwardScale,
    );
    vectorField.cache.logtimes = [0n];
    vectorField.cache.allVectors = [vector];
    vectorField.cache.load.state = "loaded";
    vectorField.meshStale = true;
    return;
  }

  const topicIdToMessagePaths: TopicIdToMessagePaths = {};
  for (const topicData of Object.values(toRead)) {
    if (typeof topicData === "number") {
      continue;
    }
    topicIdToMessagePaths[topicData.topic.id] ??= {};
    topicIdToMessagePaths[topicData.topic.id][topicData.messagePath.id] = {
      id: topicData.messagePath.id,
      parts: topicData.messagePath.parts,
    };
  }

  const topicIdToMessagePathValues = await context.dataServer.getAllData(
    topicIdToMessagePaths,
    signal,
  );
  signal.throwIfAborted();

  const vectors: babylon.Vector3[] = [];
  const logtimes: bigint[] = [];
  const xs = extractValues(topicIdToMessagePathValues, right, rightScale);
  const ys = extractValues(topicIdToMessagePathValues, up, upScale);
  const zs = extractValues(topicIdToMessagePathValues, forward, forwardScale);

  let xi = 0;
  let yi = 0;
  let zi = 0;
  do {
    const t = bigIntMax(xs.logtimes[xi], ys.logtimes[yi], zs.logtimes[zi]);
    const vector = new babylon.Vector3(
      xs.values[xi],
      ys.values[yi],
      zs.values[zi],
    );
    logtimes.push(t);
    vectors.push(vector);

    const nextXTime = xs.logtimes.at(xi + 1);
    const nextYTime = ys.logtimes.at(yi + 1);
    const nextZTime = zs.logtimes.at(zi + 1);
    const nextTime = bigIntMin(nextXTime, nextYTime, nextZTime);
    if (nextXTime === nextTime && xi < xs.logtimes.length - 1) {
      xi++;
    }
    if (nextYTime === nextTime && yi < ys.logtimes.length - 1) {
      yi++;
    }
    if (nextZTime === nextTime && zi < zs.logtimes.length - 1) {
      zi++;
    }
  } while (
    xi < xs.logtimes.length - 1 ||
    yi < ys.logtimes.length - 1 ||
    zi < zs.logtimes.length - 1
  );

  // The loop exits before emitting the sample at the final index combination.
  const tFinal = bigIntMax(xs.logtimes[xi], ys.logtimes[yi], zs.logtimes[zi]);
  logtimes.push(tFinal);
  vectors.push(
    new babylon.Vector3(xs.values[xi], ys.values[yi], zs.values[zi]),
  );

  vectorField.cache.load.state = "loaded";
  vectorField.cache.logtimes = logtimes;
  vectorField.cache.allVectors = vectors;
  vectorField.meshStale = true;
}

function extractValues(
  topicIdToData: TopicIdToMessagePathValues,
  component: TopicData | number,
  scale: number,
): { logtimes: bigint[]; values: number[] } {
  if (typeof component === "number") {
    return { logtimes: [0n], values: [component * scale] };
  } else {
    const topicData = topicIdToData[component.topic.id];
    const rawValues = topicData?.values[component.messagePath.id];

    if (!topicData || !rawValues || topicData.logtimes.length === 0) {
      return {
        logtimes: [0n],
        values: [NaN],
      };
    }

    return {
      logtimes: topicData.logtimes,
      values: rawValues.map((v) => v * scale),
    };
  }
}

function bigIntMax(...args: bigint[]) {
  return args.reduce((max, current) => (current < max ? max : current));
}

function bigIntMin(...args: (bigint | undefined)[]) {
  return args.reduce((min, current) =>
    current === undefined || (min !== undefined && current > min)
      ? min
      : current,
  );
}
